import DeleteIcon from '@mui/icons-material/Delete'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'

import Database from '../../types/Database'

interface DayHeaderProps {
  date: Date
  dayOfWeek: string
  isToday: boolean
  hasUserBooking: boolean
  canDelete: boolean
  notifications: Database.OfficeNotification[]
  onDeleteClick?: (date: Date) => void
  onNotificationClick?: (notifications: Database.OfficeNotification[]) => void
}

/**
 * DayHeader - Header section of a calendar day
 *
 * Displays the date, day of week, notification icon, and optional delete button.
 */
export default function DayHeader({
  date,
  dayOfWeek,
  isToday,
  hasUserBooking,
  canDelete,
  notifications,
  onDeleteClick,
  onNotificationClick
}: DayHeaderProps) {
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onDeleteClick) {
      onDeleteClick(date)
    }
  }

  const handleNotificationClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onNotificationClick && notifications.length > 0) {
      onNotificationClick(notifications)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}
    >
      {/* Date section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 700,
            color: isToday ? 'primary.main' : 'text.primary',
            fontSize: '1.1rem'
          }}
        >
          {date.getDate()}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              lineHeight: 1
            }}
          >
            {dayOfWeek.substring(0, 3).toUpperCase()}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ fontSize: '0.6rem', lineHeight: 1 }}
          >
            {date.toLocaleDateString('en', { month: 'short' })}
          </Typography>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {/* Notification icon */}
        {notifications.length > 0 && (
          <IconButton
            size='small'
            title={`${notifications.length} notification${
              notifications.length > 1 ? 's' : ''
            }`}
            onClick={handleNotificationClick}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark'
              },
              width: 24,
              height: 24
            }}
          >
            <NotificationsIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        )}

        {/* Delete button */}
        {hasUserBooking && canDelete && onDeleteClick && (
          <IconButton
            size='small'
            title='Cancel my booking'
            onClick={handleDeleteClick}
            sx={{
              backgroundColor: 'error.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'error.dark'
              },
              width: 24,
              height: 24
            }}
          >
            <DeleteIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  )
}
